/**
 * PlanetDetails Component
 * Detailed information about a planet shown when expanded
 */

import React from 'react';
import { calculateLocalDegreesInHouse, formatLocalDegrees } from './planetFormatters';
import { PlanetMovementSettings } from './PlanetMovementSettings';

// Draggable planets that support movement settings
const DRAGGABLE_PLANETS = ['Lg', 'Sy', 'Ch'];

export interface PlanetDetailsProps {
  planetData: {
    name: string;
    short_name: string;
    longitude: number;
    longitude_dms: string;
    latitude: number;
    speed: number;
    is_retrograde: boolean;
    rasi_number: number;
    rasi_name: string;
    nakshatra_number: number;
    nakshatra_name: string;
    nakshatra_pada: number;
    navamsa_rasi: string;
    muhurta_num?: number;
    muhurta_graha?: string;
    muhurta_lord?: string;
    [key: string]: any; // Allow additional properties for compatibility
  };
  className?: string;
  chartId?: string; // Chart ID for settings persistence
  disabled?: boolean; // If true, disable interactive features (guest mode)
}

/**
 * PlanetDetails component renders detailed information about a planet
 */
const PlanetDetails: React.FC<PlanetDetailsProps> = ({
  planetData,
  className = '',
  chartId,
  disabled = false
}) => {
  // Check if this planet supports movement settings
  const isDraggable = DRAGGABLE_PLANETS.includes(planetData.short_name);

  return (
    <div className={`planet-details ${className}`}>
      <div className="planet-details-table">
        
        {/* Position (DMS) */}
        <div className="planet-detail-row">
          <span className="planet-detail-label">Position:</span>
          <span className="planet-detail-value">
            {(() => {
              return formatLocalDegrees(calculateLocalDegreesInHouse(planetData.longitude || 0));
            })()} | <span className="">{planetData.longitude_dms || 'N/A'}</span>
          </span>
        </div>

        {/* Rasi Name */}
        <div className="planet-detail-row">
          <span className="planet-detail-label">Rasi:</span>
          <span className="planet-detail-value">{planetData.rasi_name || 'N/A'}</span>
        </div>

        {/* Nakshatra Name */}
        <div className="planet-detail-row">
          <span className="planet-detail-label">Nakshatra:</span>
          <span className="planet-detail-value">{planetData.nakshatra_name || 'N/A'} | {planetData.nakshatra_pada || 'N/A'}</span>
        </div>

        {/* Navamsa Rasi */}
        <div className="planet-detail-row">
          <span className="planet-detail-label">Navamsa:</span>
          <span className="planet-detail-value">{planetData.navamsa_rasi || 'N/A'}</span>
        </div>

        {/* Latitude - hide for Lagna, Rahu, Ketu if zero */}
        {!(Math.abs(Number(planetData.latitude || 0)) < 0.000001 && ['Lg', 'Ra', 'Ke'].includes(planetData.short_name || '')) && (
            <div className="planet-detail-row">
              <span className="planet-detail-label">Latitude:</span>
              <span className="planet-detail-value">
              {Math.abs(Number(planetData.latitude || 0)) < 0.000001
                  ? '0.000000°'
                  : Number(planetData.latitude || 0).toFixed(6) + '°'
              }
            </span>
            </div>
        )}

        {/* Speed - hide for Lagna, Rahu, Ketu if zero */}
        {!(Math.abs(Number(planetData.speed || 0)) < 0.000001 && ['Lg', 'Ra', 'Ke'].includes(planetData.short_name || '')) && (
            <div className="planet-detail-row">
              <span className="planet-detail-label">Speed:</span>
              <span className="planet-detail-value">
              {Math.abs(Number(planetData.speed || 0)) < 0.000001
                  ? '0.000000°/day'
                  : Number(planetData.speed || 0).toFixed(6) + '°/day'
              }
            </span>
            </div>
        )}

        {/* Retrograde Status */}
        <div className="planet-detail-row">
          <span className="planet-detail-label">Retrograde:</span>
          <span className="planet-detail-value">
            {planetData.is_retrograde ? (
                <span className="retrograde-text">Yes ℞</span>
            ) : (
                'No'
            )}
          </span>
        </div>

        {/* Muhurta Number */}
        {planetData.muhurta_num && (
          <div className="planet-detail-row">
            <span className="planet-detail-label">Muhurta Number:</span>
            <span className="planet-detail-value">{planetData.muhurta_num}/30</span>
          </div>
        )}

        {/* Muhurta Ruler */}
        {planetData.muhurta_graha && (
          <div className="planet-detail-row">
            <span className="planet-detail-label">Muhurta Ruler:</span>
            <span className="planet-detail-value">{planetData.muhurta_graha}</span>
          </div>
        )}

        {/* Muhurta Lord */}
        {planetData.muhurta_lord && (
          <div className="planet-detail-row">
            <span className="planet-detail-label">Muhurta Lord:</span>
            <span className="planet-detail-value">{planetData.muhurta_lord}</span>
          </div>
        )}

        {/* Movement Settings - only for draggable planets (Lg, Sy, Ch), hide in guest mode */}
        {isDraggable && chartId && !disabled && (
          <PlanetMovementSettings
            chartId={chartId}
            planet={planetData.short_name}
            disabled={disabled}
          />
        )}

      </div>
    </div>
  );
};

export default PlanetDetails;